/**
 * Admin · dead-letter queue (GET /admin/dead-letters, cursor-paged). The worker's outbox writes an
 * exhausted event into `dead_letters` once it passes MAX_ATTEMPTS. This screen makes that queue
 * visible and actionable: requeue re-enqueues the ORIGINAL outbox row (preserving the event_id
 * exactly-once dedup), discard marks it handled without re-dispatch. Both are audited server-side.
 */
import { useState } from "react";
import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../lib/api.js";
import { apiErrorText } from "./adminUi.js";

interface DeadLetter {
  id: string;
  eventId: string;
  channel: string;
  eventType: string;
  attempts: number;
  lastError: string | null;
  status: string;
  deadLetteredAt: string;
  requeuedAt: string | null;
  resolvedBy: string | null;
}

interface DeadLettersResponse {
  entries: DeadLetter[];
  nextBefore: string | null;
}

type StatusFilter = "open" | "all";

function statusPill(status: string): string {
  if (status === "open") return "pill r";
  if (status === "requeued") return "pill pv";
  return "pill n"; // discarded
}

export function DeadLetters(): ReactNode {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<StatusFilter>("open");
  // A stack of `before` cursors: empty = newest page; push to go older, pop to come back.
  const [cursors, setCursors] = useState<string[]>([]);
  const before = cursors.length > 0 ? cursors[cursors.length - 1] : null;

  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-dead-letters", status, before],
    queryFn: () =>
      api.get<DeadLettersResponse>(
        `/admin/dead-letters?status=${status}${before ? `&before=${encodeURIComponent(before)}` : ""}`,
      ),
  });
  const entries = data?.entries ?? [];

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: ["admin-dead-letters"] });
  };

  const requeue = useMutation({
    mutationFn: (eventId: string) =>
      api.post<{ requeued: boolean; outboxFound: boolean }>(
        `/admin/dead-letters/${encodeURIComponent(eventId)}/requeue`,
        {},
      ),
    onSuccess: invalidate,
  });
  const discard = useMutation({
    mutationFn: (eventId: string) =>
      api.post<{ discarded: boolean }>(`/admin/dead-letters/${encodeURIComponent(eventId)}/discard`, {}),
    onSuccess: invalidate,
  });

  // The row whose action is in flight — disables both buttons on that row only.
  const busyId =
    requeue.isPending ? requeue.variables : discard.isPending ? discard.variables : null;
  const actionError = requeue.error ?? discard.error;

  const changeStatus = (next: StatusFilter): void => {
    setStatus(next);
    setCursors([]);
  };

  return (
    <section>
      <div className="page-h">
        <div>
          <h1>Dead-letter queue</h1>
          <div className="sub">
            Events the worker gave up on after repeated failures — requeue to retry, discard to close ·
            live from the API
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <select value={status} onChange={(e) => changeStatus(e.target.value as StatusFilter)}>
            <option value="open">Open only</option>
            <option value="all">All statuses</option>
          </select>
          <span className="pill n">Platform</span>
        </div>
      </div>

      {isLoading && <div className="card pad mut sm">Loading dead-letters…</div>}

      {isError && (
        <div className="banner crit">
          <span>Couldn't load the dead-letter queue. Please retry in a moment.</span>
        </div>
      )}

      {actionError && (
        <div className="banner crit" style={{ marginBottom: 12 }}>
          <span>{apiErrorText(actionError, "Couldn't update that dead-letter. Please retry.")}</span>
        </div>
      )}

      {!isLoading && !isError && entries.length === 0 && (
        <div className="card pad">
          <p className="mut sm" style={{ margin: 0 }}>
            {status === "open"
              ? "Queue is clear — no open dead-letters. Failed events will appear here if the worker exhausts its retries."
              : "Nothing here yet."}
          </p>
        </div>
      )}

      {!isLoading && !isError && entries.length > 0 && (
        <div className="card">
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Dead-lettered</th>
                  <th>Channel</th>
                  <th>Event type</th>
                  <th>Attempts</th>
                  <th>Last error</th>
                  <th>Status</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const busy = busyId === e.eventId;
                  return (
                    <tr key={e.id}>
                      <td className="mut sm tnum" style={{ whiteSpace: "nowrap" }}>
                        {new Date(e.deadLetteredAt).toLocaleString()}
                      </td>
                      <td style={{ fontWeight: 600 }}>{e.channel}</td>
                      <td className="mut sm">{e.eventType}</td>
                      <td className="tnum">{e.attempts}</td>
                      <td className="mut sm" style={{ maxWidth: 320 }}>
                        <span title={e.lastError ?? undefined}>{e.lastError ?? "—"}</span>
                      </td>
                      <td>
                        <span className={statusPill(e.status)} style={{ textTransform: "capitalize" }}>
                          {e.status}
                        </span>
                      </td>
                      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                        {e.status === "open" ? (
                          <>
                            <button
                              className="btn sm ghost"
                              disabled={busy}
                              onClick={() => requeue.mutate(e.eventId)}
                              style={{ marginRight: 6 }}
                            >
                              {busy && requeue.isPending ? "Requeuing…" : "Requeue"}
                            </button>
                            <button
                              className="btn sm ghost"
                              disabled={busy}
                              onClick={() => discard.mutate(e.eventId)}
                            >
                              {busy && discard.isPending ? "Discarding…" : "Discard"}
                            </button>
                          </>
                        ) : (
                          <span className="mut sm">
                            {e.resolvedBy ? `by ${e.resolvedBy.slice(0, 8)}…` : "resolved"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
        <button
          className="btn sm ghost"
          disabled={cursors.length === 0}
          onClick={() => setCursors((c) => c.slice(0, -1))}
        >
          ← Newer
        </button>
        <button
          className="btn sm ghost"
          disabled={!data?.nextBefore}
          onClick={() => {
            const next = data?.nextBefore;
            if (next) setCursors((c) => [...c, next]);
          }}
        >
          Older →
        </button>
      </div>
    </section>
  );
}
